import { MouseEventHandler } from "react";

import { useQueryClient } from "@enkor/react-query";
import { useBooleanState } from "@enkor/toss-slash";

import useModalState from "@logics/hooks/useModalState";
import { ToastError, ToastSuccess } from "@logics/utils/toast";
import { ERROR_MESSAGE } from "@domains/common/constants/toastMessages";
import { PROJECT_MESSAGE } from "@domains/project/constants/toastMessages";
import { projectQueryKeys } from "@domains/project/constants/projectQueryKeys";
import { ProjectInfoTrackingDataType } from "@domains/project/types/project";
import { useCreateProjectUnregisterMutation } from "@domains/project/network/projectMutations";
import { ExclamationCircleIcon } from "@enkor/design/icons";
import { grayScale } from "@enkor/design/colors";
import WebViewBridge from "@logics/utils/webViewBridge";
import { useAuthSession } from "@domains/auth/hooks/useAuthSession";
import { LoadingSpinner } from "@enkor/design/components";

import * as S from "./CancelReasonBottomSheet.style";

const REFUND_POLICY_1 = [
  "Within 24 hours after payment: 100% refund",
  "After 24 hours from payment: 5% fee charged on refund",
];

const REFUND_POLICY_2 = [
  "14+ days before: 100% refund",
  "7 days before: 50% refund",
  "3 days before: 20% refund",
  "After that: No refund",
];

interface CancelReasonBottomSheetProps {
  projectId: string;
  projectInfo: ProjectInfoTrackingDataType;
}

const CancelReasonBottomSheet = ({
  projectId,
  projectInfo,
}: CancelReasonBottomSheetProps) => {
  const queryClient = useQueryClient();

  const { closeModal } = useModalState();

  const { user } = useAuthSession();

  const [isProcessingCancel, startProcessingCancel, finishProcessingCancel] =
    useBooleanState();

  const unregisterMutation = useCreateProjectUnregisterMutation();
  const handleSubmitReportInWeb = async () => {
    if (unregisterMutation.isPending) return;

    try {
      await unregisterMutation.mutateAsync({ projectId, content: "test_env" });

      queryClient.invalidateQueries({
        queryKey: projectQueryKeys.getProjectDetail({ projectId }),
      });
      ToastSuccess(PROJECT_MESSAGE.JOIN_CANCELLED);
      closeModal();
    } catch (error) {
      ToastError(ERROR_MESSAGE.SERVER_ERROR);
    }
  };

  const handleSubmitReport: MouseEventHandler<HTMLButtonElement> = async (
    e,
  ) => {
    e.preventDefault();
    if (isProcessingCancel) return;

    try {
      startProcessingCancel();

      const cancelResult =
        await WebViewBridge.getInstance().triggerCancelProject(
          projectInfo,
          user?.hasProfile,
        );

      if (cancelResult === "success") {
        queryClient.invalidateQueries({
          queryKey: projectQueryKeys.getProjectDetail({ projectId }),
        });

        closeModal();
      }
    } catch (error) {
      ToastError(ERROR_MESSAGE.SERVER_ERROR);
    } finally {
      finishProcessingCancel();
    }
  };

  return (
    <S.BottomSheetContainer>
      <S.HeaderWrapper>
        <S.Title name="m">Leave edition</S.Title>
        <S.CancelButton type="button" onClick={closeModal}>
          Close
        </S.CancelButton>
      </S.HeaderWrapper>
      <S.ScrollWrapper>
        <S.WarningIconWrapper>
          <ExclamationCircleIcon strokeColor={grayScale.white} />
        </S.WarningIconWrapper>
        <S.TextareaWrapper>
          <S.TextareaDescription name="strongM">
            We adhere to the following cancellation and refund policies(based on
            departure date):
          </S.TextareaDescription>

          <S.PolicyWrapper>
            <S.RefundPolicyInfo>
              {REFUND_POLICY_1.map((policy) => (
                <S.RefundPolicyItem key={policy}>{policy}</S.RefundPolicyItem>
              ))}
            </S.RefundPolicyInfo>

            <S.RefundPolicyInfo>
              {REFUND_POLICY_2.map((policy) => (
                <S.RefundPolicyItem key={policy}>{policy}</S.RefundPolicyItem>
              ))}
            </S.RefundPolicyInfo>
          </S.PolicyWrapper>

          <S.TextareaDescription name="m">
            * Leaving the edition will cancel all previously registered events.
            Please make sure to confirm before proceeding.
          </S.TextareaDescription>
        </S.TextareaWrapper>
        <S.SubmitButton
          type="button"
          disabled={unregisterMutation.isPending || isProcessingCancel}
          onClick={
            WebViewBridge.isInAppWebView()
              ? handleSubmitReport
              : handleSubmitReportInWeb
          }
        >
          {unregisterMutation.isPending || isProcessingCancel ? (
            <LoadingSpinner loaderType="PulseLoader" color={grayScale[800]} />
          ) : (
            "Confirm & Leave"
          )}
        </S.SubmitButton>
      </S.ScrollWrapper>
    </S.BottomSheetContainer>
  );
};

export default CancelReasonBottomSheet;
